import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { VehicleFormSheet } from '@/components/fleet/VehicleFormSheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/admin/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useCompanyContext } from "@/hooks/use-company-context";
import { useCostCenters } from "@/hooks/use-cost-centers";
import { useToast } from "@/hooks/use-toast";
import { validateBeforeAction } from "@/lib/plans/validation-helpers";
import { Plus, Edit, Eye, Trash2, Truck, MoreHorizontal } from "lucide-react";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFleetPermissions } from "@/hooks/use-fleet-permissions";

interface Vehicle {
  id: string;
  plate: string;
  renavam?: string;
  vin?: string;
  make?: string;
  model?: string;
  year?: number;
  category: string;
  vehicle_type_id?: string;
  fuel_type: string;
  odometer: number;
  cost_center_id?: string;
  status: string;
  created_at: string;
}

export default function FleetVehicles() {
  const navigate = useNavigate();
  const { profile, hasCompanyRole, activeCompany } = useAuth();
  const { companyId } = useCompanyContext();
  const { data: costCenters } = useCostCenters();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { canCreate, canEdit, canDelete, isViewer } = useFleetPermissions();
  const [isFormSheetOpen, setIsFormSheetOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);

  // Fetch vehicles
  const { data: vehicles = [], isLoading, error } = useQuery({
    queryKey: ["fleetVehicles", companyId],
    queryFn: async () => {
      if (!companyId) {
        console.log('🚚 [FleetVehicles] No companyId available');
        return [];
      }
      
      console.log('🚚 [FleetVehicles] Fetching vehicles for companyId:', companyId);
      
      try {
      const { data, error } = await supabase
        .from("fleet_vehicles")
        .select(`
          *,
          vehicle_type:fleet_vehicle_types(id, name, category, image_url)
        `)
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });
        
        if (error) {
          console.error('🚚 [FleetVehicles] Query error:', error);
          
          // Se for erro de RLS, mostrar mensagem específica
          if (error.code === '42501' || error.message?.includes('row-level security')) {
            toast({
              variant: "destructive",
              title: "Erro de permissão",
              description: "Você não possui acesso a esta empresa. Verifique seu vínculo e permissões."
            });
          }
          throw error;
        }
        
        console.log('🚚 [FleetVehicles] Found vehicles:', data?.length || 0);
        return data || [];
      } catch (err) {
        console.error('🚚 [FleetVehicles] Unexpected error:', err);
        throw err;
      }
    },
    enabled: !!companyId
  });

  // Create vehicle mutation
  const createVehicleMutation = useMutation({
    mutationFn: async (vehicleData: any) => {
      if (!companyId) throw new Error("No company selected");
      
      const processedData = {
        ...vehicleData,
        cost_center_id: (vehicleData.cost_center_id && vehicleData.cost_center_id !== 'none') ? vehicleData.cost_center_id : null,
        company_id: companyId,
        created_by: profile?.id
      };
      
      const { data, error } = await supabase
        .from("fleet_vehicles")
        .insert(processedData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetVehicles"] });
      setIsFormSheetOpen(false);
      setEditingVehicle(null);
      toast({
        title: "Veículo criado com sucesso!"
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar veículo",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Update vehicle mutation
  const updateVehicleMutation = useMutation({
    mutationFn: async ({ id, ...vehicleData }: { id: string } & any) => {
      const processedData = {
        ...vehicleData,
        cost_center_id: (vehicleData.cost_center_id && vehicleData.cost_center_id !== 'none') ? vehicleData.cost_center_id : null,
        updated_by: profile?.id
      };
      
      const { data, error } = await supabase
        .from("fleet_vehicles")
        .update(processedData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetVehicles"] });
      setIsFormSheetOpen(false);
      setEditingVehicle(null);
      toast({
        title: "Veículo atualizado com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar veículo",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Delete vehicle mutation
  const deleteVehicleMutation = useMutation({
    mutationFn: async (vehicleId: string) => {
      // Verificar permissão primeiro
      if (!hasCompanyRole(activeCompany?.id, 'company_admin')) {
        throw new Error('Você não tem permissão para excluir veículos. Apenas administradores da empresa podem realizar esta ação.');
      }

      const { error, count } = await supabase
        .from("fleet_vehicles")
        .delete()
        .eq("id", vehicleId);

      if (error) throw error;
      
      if (count === 0) {
        throw new Error('Veículo não encontrado ou já foi removido.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetVehicles"] });
      toast({
        title: "Veículo removido com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao remover veículo",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleFormSubmit = (data: any) => {
    if (editingVehicle) {
      updateVehicleMutation.mutate({ id: editingVehicle.id, ...data });
    } else {
      createVehicleMutation.mutate(data);
    }
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setIsFormSheetOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Ativo</Badge>;
      case 'maintenance':
        return <Badge className="bg-orange-100 text-orange-800">Manutenção</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-100 text-gray-800">Inativo</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getCategoryLabel = (category: string) => {
    const categories = {
      car: 'Carro',
      truck: 'Caminhão', 
      van: 'Van',
      pickup: 'Pickup',
      motorcycle: 'Moto'
    };
    return categories[category as keyof typeof categories] || category || 'N/A';
  };

  const getFuelTypeLabel = (fuelType: string) => {
    const types = {
      gasoline: 'Gasolina',
      diesel: 'Diesel',
      ethanol: 'Etanol',
      flex: 'Flex',
      electric: 'Elétrico',
      hybrid: 'Híbrido'
    };
    return types[fuelType as keyof typeof types] || fuelType || 'N/A';
  };

  const columns: ColumnDef<Vehicle>[] = [
    {
      accessorKey: "plate",
      header: "Placa",
      cell: ({ row }) => {
        const vehicle = row.original;
        const vehicleType = (vehicle as any).vehicle_type;
        
        return (
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-lg border border-border bg-background flex items-center justify-center overflow-hidden">
              {vehicleType?.image_url ? (
                <img
                  src={vehicleType.image_url}
                  alt={vehicleType.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <Truck className="w-7 h-7 text-muted-foreground" />
              )}
            </div>
            <span className="font-medium">{vehicle.plate}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "make",
      header: "Marca/Modelo",
      cell: ({ row }) => {
        const vehicle = row.original;
        return (
          <div>
            <div className="font-medium">{vehicle.make}</div>
            <div className="text-sm text-muted-foreground">{vehicle.model}</div>
          </div>
        );
      },
    },
    {
      accessorKey: "year",
      header: "Ano",
      cell: ({ row }) => {
        const year = row.getValue("year") as number | undefined;
        return year || "-";
      },
    },
    {
      accessorKey: "category",
      header: "Categoria",
      cell: ({ row }) => getCategoryLabel(row.getValue("category")),
    },
    {
      accessorKey: "fuel_type",
      header: "Combustível",
      cell: ({ row }) => getFuelTypeLabel(row.getValue("fuel_type")),
    },
    {
      accessorKey: "odometer",
      header: "Quilometragem",
      cell: ({ row }) => {
        const odometer = row.getValue("odometer") as number;
        return odometer ? `${odometer.toLocaleString()} km` : "0 km";
      },
    },
    {
      accessorKey: "cost_center_id",
      header: "Centro de Custo",
      cell: ({ row }) => {
        const costCenterId = row.getValue("cost_center_id") as string;
        if (!costCenterId) return "-";
        
        const costCenter = costCenters?.find(cc => cc.id === costCenterId);
        return costCenter ? `${costCenter.name}${costCenter.code ? ` (${costCenter.code})` : ''}` : costCenterId;
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => getStatusBadge(row.getValue("status")),
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => {
        const vehicle = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={() => handleEdit(vehicle)}
                disabled={!canEdit}
              >
                <Edit className="mr-2 h-4 w-4" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/company/${activeCompany?.slug}/admin/systems/fleet/vehicles/${vehicle.id}`)}>
                <Eye className="mr-2 h-4 w-4" />
                Ver detalhes
              </DropdownMenuItem>
              {canDelete && (
                <DropdownMenuItem 
                  onClick={() => deleteVehicleMutation.mutate(vehicle.id)}
                  className="text-red-600"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remover
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  if (!companyId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-lg font-semibold mb-2">Nenhuma empresa encontrada</h2>
          <p className="text-muted-foreground">
            Você precisa estar associado a uma empresa para gerenciar veículos.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Veículos</h1>
          <p className="text-muted-foreground">
            Gerencie a frota de veículos da sua empresa
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {isViewer && (
            <Badge variant="secondary">
              👁️ Modo Visualização
            </Badge>
          )}
          <Button 
            onClick={() => setIsFormSheetOpen(true)} 
            disabled={!canCreate}
          >
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Veículo
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Veículos</CardTitle>
            <Truck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{vehicles.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ativos</CardTitle>
            <div className="h-2 w-2 rounded-full bg-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {vehicles.filter(v => v.status === 'active').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Em Manutenção</CardTitle>
            <div className="h-2 w-2 rounded-full bg-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {vehicles.filter(v => v.status === 'maintenance').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inativos</CardTitle>
            <div className="h-2 w-2 rounded-full bg-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {vehicles.filter(v => v.status === 'inactive').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vehicles Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Veículos</CardTitle>
          <CardDescription>
            Gerencie todos os veículos da sua frota
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={vehicles}
            searchKey="plate"
            searchPlaceholder="Buscar por placa..."
          />
        </CardContent>
      </Card>

      <VehicleFormSheet
        open={isFormSheetOpen}
        onOpenChange={(open) => {
          setIsFormSheetOpen(open);
          if (!open) {
            setEditingVehicle(null);
          }
        }}
        companyId={companyId}
        vehicle={editingVehicle ? {
          id: editingVehicle.id,
          plate: editingVehicle.plate,
          renavam: editingVehicle.renavam,
          vin: editingVehicle.vin,
          make: editingVehicle.make,
          model: editingVehicle.model,
          year: editingVehicle.year || new Date().getFullYear(),
          category: editingVehicle.category,
          fuel_type: editingVehicle.fuel_type,
          odometer: editingVehicle.odometer,
          cost_center_id: editingVehicle.cost_center_id,
          status: editingVehicle.status
        } : undefined}
        onSubmit={handleFormSubmit}
        isLoading={createVehicleMutation.isPending || updateVehicleMutation.isPending}
        costCenters={costCenters}
      />
    </div>
  );
}